import { createAnomymousUserCookieIfNeeded } from '../utils';
import { ApiTypes, getApiDomain, processResponseText } from './api-utils';

export class APIError extends Error {
  status: number;
  code?: string;
  retryable?: boolean;
  details?: unknown;
  constructor(
    message: string,
    status: number,
    metadata?: { code?: string; retryable?: boolean; details?: unknown },
  ) {
    super();
    this.name = 'APIError';
    this.message = message;
    this.status = status;
    this.code = metadata?.code;
    this.retryable = metadata?.retryable;
    this.details = metadata?.details;
  }
}

export class NetworkError extends Error {
  url: string;
  constructor(message: string, url: string) {
    super(message);
    this.name = 'NetworkError';
    this.message = message;
    this.url = url;
  }
}

export default async function apiRequest<T>(
  apiType: ApiTypes,
  resource: string,
  options?: RequestInit,
  signal?: AbortSignal,
): Promise<{ data?: T; error?: APIError | NetworkError }> {
  // The API requires a userId cookie, even for public requests
  createAnomymousUserCookieIfNeeded();

  const base = getApiDomain(apiType);
  const url = `${base}${resource}`;
  try {
    const res = await fetch(url, {
      credentials: apiType === 'external-mogen' ? 'omit' : 'include',
      ...options,
      signal,
    });

    const responseText = await res.text();
    const data = processResponseText(responseText);

    if (!res.ok) {
      const messageFromBody =
        data &&
        typeof data === 'object' &&
        'message' in data &&
        typeof (data as { message: unknown }).message === 'string'
          ? (data as { message: string }).message
          : undefined;
      return {
        data: undefined,
        error: new APIError(
          messageFromBody || res.statusText || 'Request failed',
          res.status,
          data && typeof data === 'object'
            ? {
                code: typeof data.code === 'string' ? data.code : undefined,
                retryable:
                  typeof data.retryable === 'boolean'
                    ? data.retryable
                    : undefined,
                details: data.details,
              }
            : undefined,
        ),
      };
    }

    return { data };
  } catch (error: any) {
    return {
      data: undefined,
      error: new NetworkError(error?.message || 'Unknown error', url),
    };
  }
}
